import { useCallback, useEffect, useRef, useState } from "react";

function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [breakpoint]);
  return isMobile;
}
import axios from "axios";
import AdminDashboard from "./AdminDashboard";
import GoogleSignInButton from "./GoogleSignInButton";
import Markdown from "./Markdown";

const API_URL = process.env.REACT_APP_API_URL || "";
const TRENDING = [
  "How does ChatGPT actually work?",
  "Best countries to live in 2026",
  "How to build muscle scientifically",
  "What caused the 2008 financial crisis?",
  "How do black holes form?",
  "What is quantum entanglement?",
  "How to invest $1000 in 2026",
  "Why do we dream?",
  "How does the human immune system work?",
  "What is the meaning of life philosophically?",
];

const TAB_CATEGORIES = {
  All: "general",
  Images: "images",
  News: "news",
  Videos: "videos",
};


function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function getUpdatedStreak() {
  const today = formatDate(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = formatDate(yesterdayDate);
  const lastSearchDate = localStorage.getItem("oryyn_last_search_date");
  const savedStreak = Number(localStorage.getItem("oryyn_streak") || "0");
  if (lastSearchDate === today) return Math.max(savedStreak, 1);
  if (lastSearchDate === yesterday) return savedStreak + 1;
  return 1;
}

function saveStreak(streak) {
  localStorage.setItem("oryyn_streak", String(streak));
  localStorage.setItem("oryyn_last_search_date", formatDate(new Date()));
}

function getRandomTrending() {
  return [...TRENDING].sort(() => Math.random() - 0.5).slice(0, 5);
}

function getDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url || "source";
  }
}

function sourceBadgeClass(source) {
  return `source-badge source-badge-${source?.source_type || "web"}`;
}

function trackSourcePreference(source, profile = "personal") {
  const domain = getDomain(source?.url || "");
  if (!domain || domain === "source") return;
  const key = `oryyn_domain_prefs_${profile}`;
  const prefs = JSON.parse(localStorage.getItem(key) || "{}");
  prefs[domain] = (prefs[domain] || 0) + 1;
  localStorage.setItem(key, JSON.stringify(prefs));
}

function getPreferredDomains(profile = "personal") {
  const prefs = JSON.parse(localStorage.getItem(`oryyn_domain_prefs_${profile}`) || "{}");
  return Object.entries(prefs)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([domain]) => domain);
}

const STOPWORDS = new Set([
  "the", "a", "an", "of", "in", "on", "for", "to", "and", "or", "is", "are",
  "what", "how", "why", "when", "where", "who", "which", "that", "this",
  "with", "about", "does", "do", "did", "was", "were",
]);

function extractKeywords(text) {
  return [...new Set(
    (text || "")
      .toLowerCase()
      .match(/[a-z0-9]+/g) || []
  )].filter((word) => word.length > 2 && !STOPWORDS.has(word));
}

function isSourceRelevant(source, keywords) {
  if (!keywords.length) return true;
  const haystack = `${source?.title || ""} ${source?.snippet || ""}`.toLowerCase();
  return keywords.some((word) => haystack.includes(word));
}

function filterRelevantSources(sources, query) {
  const list = sources || [];
  const keywords = extractKeywords(query);
  const filtered = list.filter((s) => isSourceRelevant(s, keywords));
  return filtered.length > 0 ? filtered : list;
}

function answerToMarkdown(msg) {
  const sources = msg.sources || [];
  const citations = sources.length
    ? "\n\n## Sources\n" + sources.slice(0, 8).map((s, i) => `${i + 1}. [${s.title || getDomain(s.url)}](${s.url}) - ${s.source_label || "Web"}`).join("\n")
    : "";
  return `${msg.query ? `# ${msg.query}\n\n` : ""}${msg.content || ""}${citations}`;
}

// Clean, intentional-looking label for a past query (sidebar / history):
// collapse whitespace, title-case word starts, truncate with a clean ellipsis.
function formatQueryLabel(text, maxLen = 38) {
  let s = (text || "").replace(/\s+/g, " ").trim();
  if (!s) return "New search";
  s = s.replace(/(^|\s)([a-z])/g, (_m, sp, c) => sp + c.toUpperCase());
  if (s.length > maxLen) {
    s = s.slice(0, maxLen).replace(/[\s,;:.\-–—]+$/, "") + "…";
  }
  return s;
}

// Favicon with a clean letter fallback (never a broken image).
function Favicon({ domain, size = 16, className = "" }) {
  const [failed, setFailed] = useState(false);
  const clean = (domain || "").replace(/^www\./, "");
  const letter = (clean.charAt(0) || "?").toUpperCase();
  if (failed || !clean) {
    return (
      <span
        className={`favicon-fallback ${className}`.trim()}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.58) }}
        aria-hidden="true"
      >
        {letter}
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${clean}&sz=${Math.max(32, size * 2)}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function groupByDate(conversations) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const groups = {};
  for (const c of conversations) {
    const date = (c.updated_at || c.created_at || "").slice(0, 10);
    let label;
    if (date === today) label = "Today";
    else if (date === yesterday) label = "Yesterday";
    else {
      try {
        label = new Date(c.updated_at || c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      } catch {
        label = "Earlier";
      }
    }
    if (!groups[label]) groups[label] = [];
    groups[label].push(c);
  }
  return groups;
}

function useSpeechRecognition(onResult) {
  const [isListening, setIsListening] = useState(false);
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice search requires Chrome or Edge browser."); return; }
    const recognition = new SR();
    recognition.lang = "en-US";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.onresult = (event) => { onResult(event.results[0][0].transcript); };
    recognition.start();
  }, [onResult]);
  return { isListening, startListening };
}

function DarkModeToggle({ darkMode, onToggle }) {
  return (
    <button
      className="dark-mode-toggle"
      onClick={onToggle}
      type="button"
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
      title={darkMode ? "Light mode" : "Dark mode"}
    >
      {darkMode ? "☀️" : "🌙"}
    </button>
  );
}

let _oryynMarkSeq = 0;
function Logo({ size = 52 }) {
  const iconSize = Math.round(size * 0.92);
  // Unique gradient ids so multiple logos on a page don't collide
  const gid = useRef(`om${_oryynMarkSeq++}`).current;
  return (
    <div className="logo-wordmark" aria-label="Oryyn" style={{ fontSize: size, lineHeight: 1 }}>
      <svg className="logo-mark" width={iconSize} height={iconSize} viewBox="0 0 40 40" fill="none" style={{ flexShrink: 0 }}>
        <defs>
          <linearGradient id={gid} x1="4" y1="6" x2="36" y2="34" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#0d9488" />
            <stop offset="55%" stopColor="#14b8a6" />
            <stop offset="100%" stopColor="#fb7185" />
          </linearGradient>
        </defs>
        {/* Outer orbit — rotates slowly */}
        <g className="logo-orbit logo-orbit-outer" style={{ transformOrigin: "20px 20px" }}>
          <circle cx="20" cy="20" r="14" stroke={`url(#${gid})`} strokeWidth="2.6"
            fill="none" strokeLinecap="round" strokeDasharray="66 22" />
        </g>
        {/* Inner orbit — counter-rotates */}
        <g className="logo-orbit logo-orbit-inner" style={{ transformOrigin: "20px 20px" }}>
          <circle cx="20" cy="20" r="8" stroke={`url(#${gid})`} strokeWidth="2.6"
            fill="none" strokeLinecap="round" strokeDasharray="30 20" opacity="0.9" />
        </g>
        {/* Signal core — pulses */}
        <circle className="logo-core" cx="20" cy="20" r="3.4" fill={`url(#${gid})`} />
      </svg>
      <span className="logo-name">oryyn</span>
    </div>
  );
}

function SourcePanel({ sources, loading, lastQuery, category = "general", onSummarize, searchProfile = "personal" }) {
  const [extraSources, setExtraSources] = useState([]);
  const [nextPage, setNextPage] = useState(2);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    setExtraSources([]);
    setNextPage(2);
    setExhausted(false);
    setSelected([]);
  }, [lastQuery]);

  const allSources = filterRelevantSources([...(sources || []), ...extraSources], lastQuery);

  const loadMore = async () => {
    if (!lastQuery || loadingMore || exhausted) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API_URL}/more-sources`, {
        params: { query: lastQuery, category, page: nextPage },
      });
      const newSources = res.data.sources || [];
      if (newSources.length > 0) {
        setExtraSources((prev) => [...prev, ...newSources]);
        setNextPage(res.data.next_page || nextPage + 1);
      } else {
        setExhausted(true);
      }
    } catch {
      setExhausted(true);
    }
    setLoadingMore(false);
  };

  if (!loading && !allSources?.length) return null;
  return (
    <aside className="source-panel">
      <div className="source-panel-header">
        <span className="source-panel-icon">🌐</span>
        <span className="source-panel-title">{loading ? "Finding sources…" : `${allSources.length} Sources`}</span>
      </div>
      {loading && !allSources?.length
        ? [1, 2, 3].map((i) => <div key={i} className="source-skeleton" />)
        : allSources.map((source, i) => (
          <SourceCard
            key={`${source.url}-${i}`}
            source={source}
            index={i}
            selectable
            selected={selected.includes(source.url)}
            searchProfile={searchProfile}
            onToggleSelect={() => {
              setSelected((prev) => prev.includes(source.url)
                ? prev.filter((url) => url !== source.url)
                : prev.length >= 5 ? prev : [...prev, source.url]
              );
            }}
          />
        ))
      }
      {!loading && selected.length > 0 && (
        <button
          className="source-summarize"
          onClick={() => onSummarize?.(allSources.filter((source) => selected.includes(source.url)).slice(0, 5))}
          type="button"
        >
          Summarize {selected.length} selected
        </button>
      )}
      {!loading && allSources.length > 0 && lastQuery && (
        exhausted
          ? <div className="source-no-more">All results loaded</div>
          : <button className="source-load-more" onClick={loadMore} disabled={loadingMore} type="button">{loadingMore ? "Searching more…" : "Load more sources"}</button>
      )}
    </aside>
  );
}

function SourceCard({ source, index, selectable = false, selected = false, onToggleSelect, searchProfile = "personal" }) {
  const domain = getDomain(source.url);
  return (
    <a href={source.url} target="_blank" rel="noreferrer" className="source-card" onClick={() => trackSourcePreference(source, searchProfile)}>
      <div className="source-card-header">
        {selectable && (
          <span
            className={`source-check${selected ? " source-check-selected" : ""}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect?.(); }}
            role="checkbox"
            aria-checked={selected}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault(); e.stopPropagation(); onToggleSelect?.();
              }
            }}
          >
            {selected ? "✓" : ""}
          </span>
        )}
        <Favicon domain={domain} size={16} className="source-favicon" />
        <span className="source-domain">{domain}</span>
        {source.published && (
          <span className="source-date">
            {new Date(source.published).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
        <span className={sourceBadgeClass(source)}>{source.source_label || "Web"}</span>
        <span className="source-num">{index + 1}</span>
      </div>
      <div className="source-title">{source.title}</div>
      {source.snippet && <div className="source-snippet">{source.snippet}</div>}
    </a>
  );
}

function SourceCitations({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="source-citations-row">
      {sources.slice(0, 8).map((source, i) => {
        const domain = getDomain(source.url);
        return (
          <a key={`cite-${source.url}-${i}`} href={source.url} target="_blank" rel="noreferrer" className="source-citation-pill" title={source.title || domain}>
            <span className="source-citation-idx">{i + 1}</span>
            <Favicon domain={domain} size={14} className="source-favicon" />
            <span className="source-citation-domain">{domain}</span>
            <span className={sourceBadgeClass(source)}>{source.source_label || "Web"}</span>
          </a>
        );
      })}
    </div>
  );
}

function ImageGrid({ sources }) {
  const images = (sources || []).filter((s) => s.img_src || s.thumbnail);
  if (!images.length) return <div className="category-empty"><div className="category-empty-icon">🖼️</div><p>No images found. Try a different search.</p></div>;
  return (
    <div className="image-grid">
      {images.map((s, i) => (
        <a key={i} href={s.url} target="_blank" rel="noreferrer" className="image-item" title={s.title}>
          <img
            src={s.img_src || s.thumbnail}
            alt={s.title}
            loading="lazy"
            onError={(e) => { e.target.closest(".image-item").style.display = "none"; }}
          />
          <div className="image-caption">{s.title?.slice(0, 60)}</div>
        </a>
      ))}
    </div>
  );
}

function NewsCards({ sources }) {
  if (!sources?.length) return <div className="category-empty"><div className="category-empty-icon">📰</div><p>No news found. Try a different search.</p></div>;
  return (
    <div className="news-list">
      {sources.map((s, i) => {
        const domain = getDomain(s.url);
        return (
          <a key={i} href={s.url} target="_blank" rel="noreferrer" className="news-card">
            {s.thumbnail ? (
              <img src={s.thumbnail} alt="" className="news-thumb" onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <div className="news-thumb news-thumb-placeholder">
                <Favicon domain={domain} size={22} />
              </div>
            )}
            <div className="news-body">
              <div className="news-meta">
                <Favicon domain={domain} size={16} className="source-favicon" />
                <span className="news-source">{domain}</span>
                {s.published && (
                  <span className="news-date">
                    · {new Date(s.published).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
              <div className="news-title">{s.title}</div>
              {s.snippet && <div className="news-snippet">{s.snippet.slice(0, 160)}{s.snippet.length > 160 ? "…" : ""}</div>}
            </div>
          </a>
        );
      })}
    </div>
  );
}

function VideoCards({ sources }) {
  if (!sources?.length) return <div className="category-empty"><div className="category-empty-icon">🎬</div><p>No videos found. Try a different search.</p></div>;
  return (
    <div className="video-grid">
      {sources.map((s, i) => (
        <a key={i} href={s.url} target="_blank" rel="noreferrer" className="video-card">
          <div className="video-thumb-wrap">
            {s.thumbnail ? (
              <img src={s.thumbnail} alt={s.title} className="video-thumb" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <div className="video-thumb-placeholder">▶</div>
            )}
            {s.duration && <span className="video-duration">{s.duration}</span>}
          </div>
          <div className="video-info">
            <div className="video-title">{s.title}</div>
            <div className="video-source">{getDomain(s.url)}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

function ThinkingBar() {
  const statuses = ["Searching the web...", "Reading sources...", "Thinking deeply...", "Writing answer..."];
  const [statusIndex, setStatusIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStatusIndex((c) => (c + 1) % statuses.length), 1800);
    return () => clearInterval(timer);
  }, [statuses.length]);
  return (
    <div className="thinking-bar">
      <div className="thinking-dots" aria-hidden="true"><span /><span /><span /></div>
      <span>{statuses[statusIndex]}</span>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="skeleton-wrapper" aria-busy="true" aria-label="Loading search results">
      <div className="skeleton-sources-scroll">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton-source-card">
            <div className="skel-row">
              <span className="skel skel-sm-circle" />
              <span className="skel skel-sm-line" style={{ width: 90, height: 10 }} />
            </div>
            <span className="skel skel-sm-line" style={{ width: "100%", height: 11 }} />
            <span className="skel skel-sm-line" style={{ width: "75%", height: 10 }} />
          </div>
        ))}
      </div>
      <div className="skeleton-answer-card">
        <div className="skeleton-header-row">
          <span className="skel skel-avatar" />
          <span className="skel skel-label" />
        </div>
        {[100, 96, 90, 83, 55].map((w, i) => (
          <span key={i} className="skel skel-text-line" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  );
}

function FeaturedCard({ source }) {
  if (!source?.url) return null;
  const domain = getDomain(source.url);
  return (
    <a className="featured-card" href={source.url} target="_blank" rel="noreferrer">
      <div className="featured-top">
        <Favicon domain={domain} size={18} className="featured-favicon" />
        <span className="featured-domain">{domain}</span>
        <span className={sourceBadgeClass(source)}>{source.source_label || "Web"}</span>
        <span className="featured-badge">Top result</span>
        <span className="featured-go" aria-hidden="true">↗</span>
      </div>
      <h2 className="featured-title">{source.title}</h2>
      {source.snippet && <p className="featured-snippet">{source.snippet}</p>}
    </a>
  );
}

function KnowledgeCard({ msg, sources = msg.sources, isLatest, onReask }) {
  const title = formatQueryLabel(msg.query || msg.search_query || "Overview", 80);
  const initial = title.charAt(0).toUpperCase();
  const sourceTypes = [...new Set((sources || []).slice(0, 6).map((s) => s.source_label || "Web"))];
  return (
    <div className="knowledge-card">
      <div className="knowledge-card-head">
        <div className="knowledge-avatar">{initial}</div>
        <div className="knowledge-title-block">
          <div className="knowledge-eyebrow">Overview</div>
          <h3>{title}</h3>
          {sourceTypes.length > 0 && (
            <div className="knowledge-source-types">
              {sourceTypes.map((label) => <span key={label}>{label}</span>)}
            </div>
          )}
        </div>
      </div>
      <Markdown text={msg.content} className={isLatest ? "answer-fade-in" : ""} />
      <SourceCitations sources={sources} />
      <AnswerActions msg={msg} isLatest={isLatest} onReask={onReask} />
    </div>
  );
}

function ChatAnswer({ msg, isLatest, onReask }) {
  return (
    <article className="ai-answer chat-answer">
      <div className="answer-body-card">
        <Markdown text={msg.content} className={isLatest ? "answer-fade-in" : ""} />
        <AnswerActions msg={msg} isLatest={isLatest} onReask={onReask} />
      </div>
    </article>
  );
}

const SIMPLER_PROMPT = "Explain your previous answer in much simpler, beginner-friendly terms — short, plain language, no jargon.";
const DEEPER_PROMPT = "Go deeper on your previous answer: add more detail, context, concrete examples, and nuance.";

function AnswerActions({ msg, isLatest, onReask }) {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState(false);
  const [feedback, setFeedback] = useState("");

  const flash = (setter) => { setter(true); setTimeout(() => setter(false), 1400); };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(msg.content); flash(setCopied); } catch { /* noop */ }
  };

  const handleExportMarkdown = async () => {
    try { await navigator.clipboard.writeText(answerToMarkdown(msg)); flash(setCopied); } catch { /* noop */ }
  };

  const handleSave = () => {
    const savedAnswers = JSON.parse(localStorage.getItem("savedAnswers") || "[]");
    const nextSaved = [{ id: Date.now(), query: msg.query || "Oryyn answer", answer: msg.content, sources: msg.sources || [] }, ...savedAnswers];
    localStorage.setItem("savedAnswers", JSON.stringify(nextSaved));
    window.dispatchEvent(new Event("oryyn:saved"));
    flash(setSaved);
  };

  const handleShare = async () => {
    const text = `${msg.query ? msg.query + "\n\n" : ""}${msg.content}\n\nvia Oryyn — oryyn-search.pages.dev`;
    if (navigator.share) {
      try { await navigator.share({ title: "Oryyn answer", text }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(text); flash(setShared); } catch { /* noop */ }
    }
  };

  return (
    <div className="answer-actions">
      <button className="answer-action" onClick={handleCopy} type="button">{copied ? "✓ Copied" : "⧉ Copy"}</button>
      <button className="answer-action" onClick={handleExportMarkdown} type="button">MD</button>
      <button className="answer-action" onClick={handleSave} type="button">{saved ? "✓ Saved" : "🔖 Save"}</button>
      <button className="answer-action" onClick={handleShare} type="button">{shared ? "✓ Copied" : "↗ Share"}</button>
      {isLatest && onReask && (
        <>
          <button className="answer-action accent" onClick={() => onReask(SIMPLER_PROMPT)} type="button">✨ Simpler</button>
          <button className="answer-action accent" onClick={() => onReask(DEEPER_PROMPT)} type="button">⤵ Go deeper</button>
        </>
      )}
      <span className="answer-actions-spacer" />
      {feedback ? (
        <span className="answer-feedback-thanks">{feedback}</span>
      ) : (
        <>
          <button className="answer-action icon-only" onClick={() => setFeedback("Thanks for the feedback!")} type="button" aria-label="Helpful">👍</button>
          <button className="answer-action icon-only" onClick={() => setFeedback("Thanks — we'll improve!")} type="button" aria-label="Not helpful">👎</button>
        </>
      )}
    </div>
  );
}

function AIAnswer({ msg, isLatest, hideSources = false, onReask }) {
  const category = msg.category || "general";
  const answerType = msg.answer_type || (category === "chat" ? "chat" : "search");

  if (answerType === "chat" || category === "chat") {
    return <ChatAnswer msg={msg} isLatest={isLatest} onReask={onReask} />;
  }

  if (category === "images") {
    return <article className="ai-answer"><ImageGrid sources={msg.sources} /></article>;
  }

  if (category === "videos") {
    return <article className="ai-answer"><VideoCards sources={msg.sources} /></article>;
  }

  // general/entity/news only: filter out sources with no keyword overlap with the query
  const sources = filterRelevantSources(msg.sources, msg.query || msg.search_query || "");

  if (category === "news") {
    return (
      <article className="ai-answer">
        <NewsCards sources={sources} />
        {msg.content && (
          <>
            <SourceCitations sources={sources} />
            <div className="answer-body-card" style={{ marginTop: 20 }}>
              <Markdown text={msg.content} className={isLatest ? "answer-fade-in" : ""} />
              <AnswerActions msg={msg} isLatest={isLatest} onReask={onReask} />
            </div>
          </>
        )}
      </article>
    );
  }

  if (answerType === "entity") {
    return (
      <article className="ai-answer">
        {msg.content && <KnowledgeCard msg={msg} sources={sources} isLatest={isLatest} onReask={onReask} />}
        {(msg.news || []).slice(0, 6).length > 0 && (
          <section className="dash-news">
            <h3 className="dash-head">Latest news</h3>
            <NewsCards sources={(msg.news || []).slice(0, 6)} />
          </section>
        )}
      </article>
    );
  }

  // ─── General: search layout — featured site → news cards → summary ──
  const topSource = sources?.[0];
  const dashNews = (msg.news || []).slice(0, 6);
  return (
    <article className="ai-answer">
      {topSource && <FeaturedCard source={topSource} />}

      {dashNews.length > 0 && (
        <section className="dash-news">
          <h3 className="dash-head">Latest news</h3>
          <NewsCards sources={dashNews} />
        </section>
      )}

      {msg.content && (
        <div className="answer-body-card answer-summary">
          {!hideSources && <SourceCitations sources={sources} />}
          <Markdown text={msg.content} className={isLatest ? "answer-fade-in" : ""} />
          <AnswerActions msg={msg} isLatest={isLatest} onReask={onReask} />
        </div>
      )}
    </article>
  );
}

function RelatedQuestions({ questions, onAsk }) {
  if (!questions?.length) return null;
  return (
    <section className="related-questions">
      <h3>People also ask</h3>
      {questions.map((question) => (
        <button key={question} className="related-question" onClick={() => onAsk(question)} type="button">
          <span>↗</span><strong>{question}</strong>
        </button>
      ))}
    </section>
  );
}

function SearchStreak({ streak }) {
  if (streak < 2) return null;
  return <div className="streak-badge">🔥 {streak} day streak</div>;
}

function ProfileSwitch({ value, onChange }) {
  return (
    <div className="profile-switch" role="group" aria-label="Search profile">
      {["personal", "work"].map((profile) => (
        <button
          key={profile}
          className={value === profile ? "active" : ""}
          onClick={() => onChange(profile)}
          type="button"
        >
          {profile === "personal" ? "Personal" : "Work"}
        </button>
      ))}
    </div>
  );
}

function TrendCard({ topic, onSearch }) {
  return (
    <button className="trend-card" onClick={() => onSearch(topic.title)} type="button">
      <span className="trend-chip">{topic.category}</span>
      <h3 className="trend-title">{topic.title}</h3>
      {topic.answer && <p className="trend-answer">{topic.answer}</p>}
      {topic.sources?.length > 0 && (
        <div className="trend-sources">
          {topic.sources.slice(0, 4).map((s, i) => (
            <span className="trend-source" key={`${s.domain}-${i}`}>
              <img
                src={`https://www.google.com/s2/favicons?domain=${s.domain}&sz=32`}
                alt=""
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <span className="trend-source-domain">{s.domain}</span>
            </span>
          ))}
        </div>
      )}
      <span className="trend-go" aria-hidden="true">→</span>
    </button>
  );
}

function TrendCardSkeleton() {
  return (
    <div className="trend-card trend-card-skel" aria-hidden="true">
      <span className="skel" style={{ width: 56, height: 18, borderRadius: 6 }} />
      <span className="skel" style={{ width: "85%", height: 16, borderRadius: 5, marginTop: 12 }} />
      <span className="skel" style={{ width: "60%", height: 16, borderRadius: 5, marginTop: 6 }} />
      <span className="skel" style={{ width: "100%", height: 12, borderRadius: 4, marginTop: 12 }} />
      <span className="skel" style={{ width: "70%", height: 12, borderRadius: 4, marginTop: 6 }} />
    </div>
  );
}

function TrendingFeed({ onSearch }) {
  const [topics, setTopics] = useState(null);
  const [failed, setFailed] = useState(false);
  const topicsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios
        .get(`${API_URL}/trending`, { params: { t: Date.now() } })
        .then((res) => {
          if (cancelled) return;
          const t = res.data?.topics || [];
          if (t.length) { setTopics(t); topicsRef.current = t; setFailed(false); }
          else if (!topicsRef.current) setFailed(true);
        })
        .catch(() => { if (!cancelled && !topicsRef.current) setFailed(true); });
    };
    load();
    // Refresh trending every 4 minutes so the feed stays live
    const timer = setInterval(load, 240000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Graceful fallback: the old static list if the live feed is unavailable
  if (failed) {
    return (
      <section className="trending-feed">
        <div className="trending-head"><h2>Trending on Oryyn</h2></div>
        <div className="trending-list">
          {getRandomTrending().map((item) => (
            <button key={item} onClick={() => onSearch(item)} type="button">
              <span>📈</span><strong>{item}</strong>
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="trending-feed">
      <div className="trending-head">
        <h2>Trending now</h2>
        <span className="trending-live"><span className="trending-live-dot" />Live from the web</span>
      </div>
      <div className="trending-grid">
        {topics
          ? topics.map((t, i) => <TrendCard key={i} topic={t} onSearch={onSearch} />)
          : [0, 1, 2, 3, 4, 5].map((i) => <TrendCardSkeleton key={i} />)}
      </div>
    </section>
  );
}

function AdBanner({ isPro }) {
  if (isPro) return null;
  return (
    <div className="ad-banner">
      <span style={{ fontSize: 10, background: "#fbbc04", color: "var(--text)", padding: "2px 6px", borderRadius: 3, fontWeight: 700, marginTop: 3, flexShrink: 0 }}>Ad</span>
      <div>
        <a href="/pro">Oryyn Pro — Unlimited AI Search, No Ads</a>
        <div className="ad-banner-url">oryyn.ai/pro</div>
        <div className="ad-banner-desc">Unlimited searches, faster responses, and zero ads. Starting at $9.99/month.</div>
      </div>
    </div>
  );
}

function SavedAnswers({ open, onClose }) {
  const [savedAnswers, setSavedAnswers] = useState([]);
  const loadSavedAnswers = useCallback(() => {
    setSavedAnswers(JSON.parse(localStorage.getItem("savedAnswers") || "[]"));
  }, []);
  useEffect(() => {
    loadSavedAnswers();
    window.addEventListener("oryyn:saved", loadSavedAnswers);
    window.addEventListener("storage", loadSavedAnswers);
    return () => { window.removeEventListener("oryyn:saved", loadSavedAnswers); window.removeEventListener("storage", loadSavedAnswers); };
  }, [loadSavedAnswers]);
  const deleteAnswer = (id) => {
    const next = savedAnswers.filter((a) => a.id !== id);
    localStorage.setItem("savedAnswers", JSON.stringify(next));
    setSavedAnswers(next);
  };
  if (!open) return null;
  return (
    <>
      <button className="saved-backdrop" onClick={onClose} type="button" aria-label="Close" />
      <aside className="saved-panel">
        <div className="saved-panel-header">
          <div><span>Saved</span><h2>Saved Answers</h2></div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close">×</button>
        </div>
        {savedAnswers.length === 0 ? (
          <p className="empty-saved">No saved answers yet. Hit 🔖 on any answer to save it.</p>
        ) : (
          <div className="saved-list">
            {savedAnswers.map((item) => (
              <article className="saved-item" key={item.id}>
                <div><h3>{formatQueryLabel(item.query, 52)}</h3><p>{item.answer.slice(0, 100)}{item.answer.length > 100 ? "…" : ""}</p></div>
                <button onClick={() => deleteAnswer(item.id)} type="button">Delete</button>
              </article>
            ))}
          </div>
        )}
      </aside>
    </>
  );
}

function SearchHistoryPanel({ open, onClose, onSearch }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("oryyn_token");
    if (!token) { setError("Sign in to view your search history."); return; }
    setLoading(true); setError("");
    axios.get(`${API_URL}/user/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setHistory(res.data.searches || []))
      .catch(() => setError("Could not load history."))
      .finally(() => setLoading(false));
  }, [open]);
  if (!open) return null;
  return (
    <>
      <button className="saved-backdrop" onClick={onClose} type="button" aria-label="Close" />
      <aside className="saved-panel">
        <div className="saved-panel-header">
          <div><span>History</span><h2>Search History</h2></div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close">×</button>
        </div>
        {loading && <p style={{ textAlign: "center", color: "var(--text-2)", padding: 24 }}>Loading...</p>}
        {error && <p style={{ color: "#c5221f", padding: "12px 16px", fontSize: 13 }}>{error}</p>}
        {!loading && !error && history.length === 0 && <p className="empty-saved">No search history yet.</p>}
        {!loading && !error && history.length > 0 && (
          <div className="saved-list">
            {history.map((item, i) => (
              <article className="saved-item" key={item.id || i} style={{ cursor: "pointer" }} onClick={() => { onSearch(item.query); onClose(); }} title={item.query}>
                <div>
                  <h3>{formatQueryLabel(item.query, 52)}</h3>
                  <p style={{ fontSize: 12, color: "var(--text-3)" }}>{item.created_at ? new Date(item.created_at).toLocaleString() : ""}{item.response_time_ms ? ` · ${item.response_time_ms}ms` : ""}</p>
                </div>
                <span style={{ fontSize: 12, color: item.success ? "#34A853" : "#EA4335", flexShrink: 0 }}>{item.success ? "✓" : "✗"}</span>
              </article>
            ))}
          </div>
        )}
      </aside>
    </>
  );
}

function SettingsModal({ user, onClose, onUpdate }) {
  const [fullName, setFullName] = useState(user.full_name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function handleSave() {
    setLoading(true); setError(""); setSuccess("");
    try {
      const token = localStorage.getItem("oryyn_token");
      const body = { full_name: fullName || null };
      if (newPassword) { body.current_password = currentPassword; body.new_password = newPassword; }
      const res = await axios.patch(`${API_URL}/user/settings`, body, { headers: { Authorization: `Bearer ${token}` } });
      const updatedUser = { ...user, full_name: res.data.full_name };
      localStorage.setItem("oryyn_user", JSON.stringify(updatedUser));
      onUpdate(updatedUser);
      setSuccess("Settings saved!"); setCurrentPassword(""); setNewPassword("");
    } catch (err) { setError(err.response?.data?.detail || "Failed to save settings"); }
    setLoading(false);
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: 440 }}>
        <h2 className="modal-heading">Account Settings</h2>
        {error && <div className="modal-error">{error}</div>}
        {success && <div className="modal-success">{success}</div>}
        <label className="modal-label">Display Name</label>
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" className="modal-input" />
        <label className="modal-label">Email (read-only)</label>
        <input value={user.email} disabled className="modal-input" />
        <label className="modal-label">Plan</label>
        <div className={`modal-plan-box ${user.is_pro ? "pro" : "free"}`}>
          {user.is_pro ? "⭐ Oryyn Pro" : "Free Plan · Upgrade at /pro"}
        </div>
        <div className="modal-divider">
          <p className="modal-section-title">Change Password</p>
          <input type="password" placeholder="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="modal-input" />
          <input type="password" placeholder="New password (min 8 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="modal-input" style={{ marginBottom: 0 }} />
        </div>
        <button onClick={handleSave} disabled={loading} style={{ width: "100%", padding: 12, background: loading ? "#ccc" : "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", fontFamily: "Nunito, sans-serif" }} type="button">
          {loading ? "Saving..." : "Save Changes"}
        </button>
        <button onClick={onClose} className="modal-close" type="button">✕</button>
      </div>
    </div>
  );
}

function SearchSuggestions({ query, suggestions, onSelect }) {
  if (!query || suggestions.length === 0) return null;
  return (
    <div className="search-suggestions">
      {suggestions.map((s, i) => (
        <button key={i} className="suggestion-item" onClick={() => onSelect(s)} type="button">
          <span className="suggestion-icon">🔍</span>
          <span className="suggestion-text">{s}</span>
        </button>
      ))}
    </div>
  );
}

function SearchBox({ query, setQuery, onSearch, inputRef, compact = false, onMicClick, isListening }) {
  const suggestions = query ? TRENDING.filter(t => t.toLowerCase().includes(query.toLowerCase())).slice(0, 5) : [];

  return (
    <div className={`search-box-wrapper ${compact ? "compact" : ""}`}>
      <div className={`search-box ${compact ? "compact" : ""}`}>
        <span className="search-icon">🔍</span>
        <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSearch(query); }} placeholder="Ask Oryyn anything" />
        {query && <button className="clear-button" onClick={() => setQuery("")} type="button" aria-label="Clear">×</button>}
        <button className={`mic-button${isListening ? " mic-listening" : ""}`} onClick={onMicClick} type="button" aria-label={isListening ? "Listening..." : "Voice search"} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: "0 6px", opacity: isListening ? 1 : 0.6, transition: "opacity 0.2s", color: isListening ? "#EA4335" : "inherit" }}>🎤</button>
      </div>
      {query && <SearchSuggestions query={query} suggestions={suggestions} onSelect={(s) => { setQuery(s); onSearch(s); }} />}
    </div>
  );
}

// Small, subtle inline field for asking a follow-up on the results page —
// intentionally not a full SearchBox (no mic/clear) so it doesn't compete
// with the sticky top navbar's search box.
function FollowUpBar({ onSearch }) {
  const [text, setText] = useState("");

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSearch(trimmed);
    setText("");
  };

  return (
    <form
      className="follow-up-bar"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Ask a follow-up"
        aria-label="Ask a follow-up"
      />
      <button type="submit" className="follow-up-submit" aria-label="Ask" disabled={!text.trim()}>↵</button>
    </form>
  );
}

function AuthModal({ mode, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit() {
    setLoading(true); setError("");
    try {
      const endpoint = mode === "signup" ? "/auth/signup" : "/auth/login";
      const body = mode === "signup" ? { email, password, full_name: fullName } : { email, password };
      const res = await axios.post(`${API_URL}${endpoint}`, body);
      localStorage.setItem("oryyn_token", res.data.token);
      localStorage.setItem("oryyn_user", JSON.stringify(res.data.user));
      onSuccess(res.data.user);
    } catch (err) { setError(err.response?.data?.detail || "Something went wrong"); }
    setLoading(false);
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Logo size={32} />
          <h2 style={{ marginTop: 12, fontSize: 20, color: "var(--text)" }}>{mode === "signup" ? "Create account" : "Sign in to Oryyn"}</h2>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <GoogleSignInButton onSuccess={onSuccess} onError={setError} />
        {mode === "signup" && <input placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="modal-input-auth" />}
        <input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="modal-input-auth" />
        <input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }} className="modal-input-auth" style={{ marginBottom: 20 }} />
        <button onClick={handleSubmit} disabled={loading} style={{ width: "100%", padding: "12px", background: loading ? "#ccc" : "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", fontFamily: "Nunito, sans-serif", marginBottom: 16 }} type="button">
          {loading ? "Please wait..." : mode === "signup" ? "Create Account" : "Sign In"}
        </button>
        <div style={{ textAlign: "center", fontSize: 13, color: "var(--text-2)" }}>
          {mode === "signup" ? "Already have an account? " : "New to Oryyn? "}
          <button onClick={() => onClose(mode === "signup" ? "login" : "signup")} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 13, fontWeight: 700 }} type="button">
            {mode === "signup" ? "Sign in" : "Create account"}
          </button>
        </div>
        <button onClick={() => onClose(null)} className="modal-close" type="button">✕</button>
      </div>
    </div>
  );
}

function ConversationItem({ conversation, active, onLoad, onDelete }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`conv-item${active ? " conv-item-active" : ""}`}
      onClick={onLoad}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onLoad(); }}
    >
      <span className="conv-item-title">{formatQueryLabel(conversation.title, 34)}</span>
      {hovered && (
        <button
          className="conv-item-delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete"
          type="button"
        >🗑</button>
      )}
    </div>
  );
}

function Sidebar({ user, conversations, activeConversationId, onNewChat, onLoad, onDelete, open, onToggle, darkMode, onToggleDarkMode }) {
  const grouped = groupByDate(conversations);
  return (
    <aside className={`sidebar${open ? "" : " sidebar-hidden"}`}>
      <div className="sidebar-header">
        <Logo size={18} />
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DarkModeToggle darkMode={darkMode} onToggle={onToggleDarkMode} />
          <button className="sidebar-collapse-btn" onClick={onToggle} title="Collapse sidebar" type="button">←</button>
        </div>
      </div>

      <button className="new-chat-btn" onClick={onNewChat} type="button">
        <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> New Search
      </button>

      <div className="sidebar-convs">
        {Object.entries(grouped).map(([date, convs]) => (
          <div key={date} className="conv-group">
            <div className="conv-date-label">{date}</div>
            {convs.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                active={conv.id === activeConversationId}
                onLoad={() => onLoad(conv.id)}
                onDelete={() => onDelete(conv.id)}
              />
            ))}
          </div>
        ))}
        {conversations.length === 0 && (
          <p className="sidebar-empty">No searches yet. Start exploring!</p>
        )}
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar">{user?.email?.[0]?.toUpperCase()}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.full_name || "Oryyn User"}</div>
            <div className="sidebar-user-email">{user?.email}</div>
          </div>
          {user?.is_pro && (
            <span style={{ fontSize: 9, background: "#FFF3CD", color: "#856404", borderRadius: 4, padding: "2px 5px", fontWeight: 800, border: "1px solid #FFD700", flexShrink: 0 }}>PRO</span>
          )}
        </div>
      </div>
    </aside>
  );
}

function UserMenu({ user, onLogout, onShowHistory, onShowSaved, onShowSettings, onClearMemory }) {
  const [open, setOpen] = useState(false);
  const [managingPortal, setManagingPortal] = useState(false);

  async function handleManageSubscription() {
    const token = localStorage.getItem("oryyn_token");
    if (!token) return;
    setManagingPortal(true);
    try {
      const res = await axios.post(`${API_URL}/pro/portal`, {}, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch { window.location.href = "/pro"; }
    finally { setManagingPortal(false); }
  }

  const menuItems = [
    { label: "Search History", icon: "🕐", onClick: () => { setOpen(false); onShowHistory(); } },
    { label: "Saved Answers", icon: "🔖", onClick: () => { setOpen(false); onShowSaved(); } },
    { label: "Settings", icon: "⚙️", onClick: () => { setOpen(false); onShowSettings(); } },
    { label: "Clear Memory", icon: "🧹", onClick: () => { setOpen(false); onClearMemory(); } },
  ];

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6 }}>
      {user.is_pro && <span style={{ fontSize: 10, background: "#FFF3CD", color: "#856404", borderRadius: 4, padding: "2px 6px", fontWeight: 800, border: "1px solid #FFD700" }}>PRO</span>}
      <button onClick={() => setOpen(!open)} style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent)", color: "#fff", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700 }} type="button">
        {user.email[0].toUpperCase()}
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 499 }} onClick={() => setOpen(false)} />
          <div className="user-menu-dropdown">
            <div className="user-menu-header">
              <div className="user-menu-name">{user.full_name || "Oryyn User"}</div>
              <div className="user-menu-email">{user.email}</div>
            </div>
            {menuItems.map((item) => (
              <button key={item.label} onClick={item.onClick} className="user-menu-item" type="button">
                {item.icon} {item.label}
              </button>
            ))}
            <div className="user-menu-divider">
              {!user.is_pro ? (
                <button onClick={() => { window.location.href = "/pro"; }} className="user-menu-item upgrade" type="button">
                  ⚡ Upgrade to Pro ✨
                </button>
              ) : (
                <button onClick={handleManageSubscription} disabled={managingPortal} className="user-menu-item" style={{ color: "var(--accent)", marginBottom: 4 }} type="button">
                  ✨ {managingPortal ? "Loading..." : "Manage Subscription"}
                </button>
              )}
              <button onClick={onLogout} className="user-menu-item danger" type="button">
                🚪 Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function App() {
  if (window.location.pathname === "/admin") return <AdminDashboard />;

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  const [streak, setStreak] = useState(Number(localStorage.getItem("oryyn_streak") || "0"));
  const [showSaved, setShowSaved] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [totalSearches, setTotalSearches] = useState(Number(localStorage.getItem("oryyn_total_searches") || "0"));
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [searchProfile, setSearchProfile] = useState(() => localStorage.getItem("oryyn_search_profile") || "personal");

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("oryyn_dark_mode") === "true");

  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isMobile = useIsMobile(720);

  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const lastQueryRef = useRef("");
  const activeCategoryRef = useRef("general");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
    localStorage.setItem("oryyn_dark_mode", String(darkMode));
  }, [darkMode]);

  // Animated theme toggle: a circular reveal sweeps the new theme across the
  // page from the toggle's position (View Transitions API; instant fallback).
  const toggleTheme = useCallback((event) => {
    const next = !darkMode;
    const apply = () => {
      document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
      setDarkMode(next);
    };

    // Spin the toggle icon (independent of the transition support)
    const btn = event?.currentTarget;
    if (btn && btn.classList) {
      btn.classList.remove("toggle-spin");
      void btn.offsetWidth; // restart the animation
      btn.classList.add("toggle-spin");
    }

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!document.startViewTransition || reduce) {
      apply();
      return;
    }

    const x = event?.clientX ?? window.innerWidth - 36;
    const y = event?.clientY ?? 36;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    // A soft brand-colored bloom that leads the reveal
    const bloom = document.createElement("span");
    bloom.className = "theme-bloom";
    bloom.style.left = `${x}px`;
    bloom.style.top = `${y}px`;
    document.body.appendChild(bloom);
    bloom.addEventListener("animationend", () => bloom.remove());

    const transition = document.startViewTransition(apply);
    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        { duration: 620, easing: "cubic-bezier(.65,0,.35,1)", pseudoElement: "::view-transition-new(root)" }
      );
    });
  }, [darkMode]);

  useEffect(() => {
    const token = localStorage.getItem("oryyn_token");
    const savedUser = localStorage.getItem("oryyn_user");
    if (token && savedUser) setUser(JSON.parse(savedUser));
    setStreak(getUpdatedStreak());
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== "input" && tag !== "textarea") {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
      if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const cards = Array.from(document.querySelectorAll(".source-card"));
        const active = document.activeElement;
        const idx = cards.indexOf(active);
        if (idx >= 0 || active === inputRef.current) {
          e.preventDefault();
          const next = e.key === "ArrowDown"
            ? Math.min(cards.length - 1, idx + 1)
            : Math.max(0, idx - 1);
          cards[next]?.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    localStorage.setItem("oryyn_search_profile", searchProfile);
  }, [searchProfile]);

  useEffect(() => {
    if (user) loadConversations();
    else setConversations([]);
  }, [user]);

  useEffect(() => {
    // Only auto-scroll for follow-up queries (not first search — page already shows from top)
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.role === "assistant" && !lastMsg.loading && messages.length > 2) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages]);

  const loadConversations = async () => {
    const token = localStorage.getItem("oryyn_token");
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/user/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(res.data.conversations || []);
    } catch {}
  };

  const loadConversation = async (conversationId) => {
    const token = localStorage.getItem("oryyn_token");
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/user/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const msgs = res.data.messages || [];
      const displayMessages = [];
      for (const msg of msgs) {
        if (msg.role === "user") {
          displayMessages.push({ role: "user", content: msg.content });
        } else {
          displayMessages.push({ role: "assistant", content: msg.content, sources: [], loading: false });
        }
      }
      setMessages(displayMessages);
      setActiveConversationId(conversationId);
      setStarted(displayMessages.length > 0);
      setRelated([]);
      setError("");
      setActiveTab("All");
      activeCategoryRef.current = "general";
    } catch {}
  };

  const handleDeleteConversation = async (conversationId) => {
    const token = localStorage.getItem("oryyn_token");
    if (!token) return;
    try {
      await axios.delete(`${API_URL}/user/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (activeConversationId === conversationId) startNewChat();
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    } catch {}
  };

  const startNewChat = () => {
    setActiveConversationId(null);
    setMessages([]);
    setRelated([]);
    setStarted(false);
    setError("");
    setQuery("");
    setActiveTab("All");
    activeCategoryRef.current = "general";
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const logout = () => {
    localStorage.removeItem("oryyn_token");
    localStorage.removeItem("oryyn_user");
    setUser(null);
    startNewChat();
  };

  const clearMemory = async () => {
    if (!window.confirm("Delete all conversation history? This cannot be undone.")) return;
    const token = localStorage.getItem("oryyn_token");
    try {
      await axios.delete(`${API_URL}/user/conversation`, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    setConversations([]);
    startNewChat();
  };

  const search = useCallback(
    async (rawQuery) => {
      const searchQuery = rawQuery.trim();
      if (!searchQuery || loading) return;

      const nextTotalSearches = totalSearches + 1;
      localStorage.setItem("oryyn_total_searches", String(nextTotalSearches));
      setTotalSearches(nextTotalSearches);

      const nextStreak = getUpdatedStreak();
      saveStreak(nextStreak);
      setStreak(nextStreak);

      lastQueryRef.current = searchQuery;

      const userMessage = { role: "user", content: searchQuery };
      const currentCategory = activeCategoryRef.current || "general";
      const assistantPlaceholder = { role: "assistant", content: "", loading: true, sources: [], query: searchQuery, category: currentCategory, answer_type: "search" };
      const history = [...messages, userMessage].slice(-12);

      setStarted(true);
      setLoading(true);
      setError("");
      setRelated([]);
      setQuery("");
      setMessages((current) => [...current, userMessage, assistantPlaceholder]);

      const token = localStorage.getItem("oryyn_token");
      const body = {
        query: searchQuery,
        history,
        category: activeCategoryRef.current,
        conversation_id: activeConversationId,
        search_profile: searchProfile,
        preferred_domains: getPreferredDomains(searchProfile),
      };

      // Update only the last (assistant placeholder) message
      const patchLast = (patch) =>
        setMessages((current) =>
          current.map((message, index) =>
            index === current.length - 1 ? { ...message, ...patch } : message
          )
        );

      // Attempt 1: streaming. Falls back to the blocking endpoint on any failure.
      const streamSearch = async () => {
        const resp = await fetch(`${API_URL}/search/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(body),
        });
        if (!resp.ok || !resp.body) {
          const e = new Error("stream-unavailable");
          e.response = { status: resp.status };
          throw e;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let acc = "";
        let gotToken = false;
        let streamErr = null;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;
            let evt;
            try { evt = JSON.parse(raw); } catch { continue; }
            if (evt.type === "sources") {
              patchLast({
                sources: evt.sources || [],
                news: evt.news || [],
                category: evt.category || currentCategory,
                freshness_date: evt.freshness_date,
                search_query: evt.search_query,
                answer_type: evt.answer_type || "search",
              });
            } else if (evt.type === "token") {
              acc += evt.text; gotToken = true;
              // Clear loading on first token so the answer card renders and
              // streams in (instead of the skeleton hiding it).
              patchLast({ content: acc, loading: false });
            } else if (evt.type === "replace") {
              acc = evt.text; gotToken = true;
              patchLast({
                content: acc,
                loading: false,
                category: evt.category || currentCategory,
                answer_type: evt.answer_type || "search",
              });
            } else if (evt.type === "done") {
              setRelated(evt.related || []);
              patchLast({
                loading: false,
                category: evt.category || currentCategory,
                freshness_date: evt.freshness_date,
                search_query: evt.search_query,
                answer_type: evt.answer_type || "search",
              });
              if (evt.conversation_id) {
                setActiveConversationId(evt.conversation_id);
                loadConversations();
              }
            } else if (evt.type === "error") {
              streamErr = evt;
            }
          }
        }
        if (streamErr) {
          const e = new Error(streamErr.detail || "stream error");
          e.code = streamErr.code;
          throw e;
        }
        const mediaCategory = body.category === "images" || body.category === "videos";
        if (!gotToken && !mediaCategory) throw new Error("empty-stream");
      };

      // Attempt 2: original blocking endpoint
      const blockingSearch = async () => {
        const response = await axios.post(`${API_URL}/search`, body, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        patchLast({
          content: response.data.answer,
          sources: response.data.sources || [],
          news: response.data.news || [],
          loading: false,
          category: response.data.category || currentCategory,
          freshness_date: response.data.freshness_date,
          search_query: response.data.search_query,
          answer_type: response.data.answer_type || "search",
        });
        setRelated(response.data.related || []);
        if (response.data.conversation_id) {
          setActiveConversationId(response.data.conversation_id);
          loadConversations();
        }
      };

      try {
        try {
          await streamSearch();
        } catch (streamError) {
          // Don't retry the blocking path on rate limits — it just burns
          // more tokens and fails again. Surface the message instead.
          if (streamError.response?.status === 429 || streamError.code === "rate_limit") throw streamError;
          await blockingSearch();
        }
      } catch (requestError) {
        let friendlyError;
        if (requestError.code === "rate_limit") {
          friendlyError = requestError.message;
        } else if (requestError.response?.status === 429) {
          friendlyError = requestError.response?.data?.detail
            || (user?.is_pro
              ? "You're searching too fast. Please wait a moment."
              : "Free search limit reached (10/min). Upgrade to Pro for unlimited searches.");
        } else {
          friendlyError = requestError.response?.data?.detail || "Oryyn could not reach the search backend. Make sure the API and SearXNG are running.";
        }
        setError(friendlyError);
        setMessages((current) =>
          current.map((message, index) =>
            index === current.length - 1 ? { ...message, content: friendlyError, loading: false, sources: [] } : message
          )
        );
      } finally {
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [loading, messages, totalSearches, user, activeConversationId, searchProfile]
  );

  const { isListening, startListening } = useSpeechRecognition(
    useCallback((transcript) => { if (transcript.trim()) search(transcript); }, [search])
  );

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    activeCategoryRef.current = TAB_CATEGORIES[tab] || "general";
    if (lastQueryRef.current) search(lastQueryRef.current);
  }, [search]);

  const summarizeSelectedSources = useCallback(async (selectedSources) => {
    if (!selectedSources?.length || loading) return;
    const lastQuery = messages.filter((m) => m.role === "user").at(-1)?.content || lastQueryRef.current || "Selected sources";
    const placeholder = {
      role: "assistant",
      content: "",
      loading: true,
      sources: selectedSources,
      query: `Summary of ${selectedSources.length} selected sources`,
      category: "general",
    };
    setMessages((current) => [...current, placeholder]);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/summarize-sources`, {
        query: lastQuery,
        sources: selectedSources,
      });
      setMessages((current) => current.map((message, index) =>
        index === current.length - 1
          ? {
            ...message,
            content: response.data.answer,
            sources: response.data.sources || selectedSources,
            freshness_date: response.data.freshness_date,
            loading: false,
          }
          : message
      ));
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not summarize the selected sources.";
      setMessages((current) => current.map((message, index) =>
        index === current.length - 1 ? { ...message, content: msg, loading: false } : message
      ));
    } finally {
      setLoading(false);
    }
  }, [loading, messages]);

  const isPro = user?.is_pro || false;

  const userMenuProps = {
    user,
    onLogout: logout,
    onShowHistory: () => setShowHistory(true),
    onShowSaved: () => setShowSaved(true),
    onShowSettings: () => setShowSettings(true),
    onClearMemory: clearMemory,
  };

  const modals = (
    <>
      <SavedAnswers open={showSaved} onClose={() => setShowSaved(false)} />
      <SearchHistoryPanel open={showHistory} onClose={() => setShowHistory(false)} onSearch={search} />
      {showSettings && user && <SettingsModal user={user} onClose={() => setShowSettings(false)} onUpdate={setUser} />}
      {showAuth && (
        <AuthModal
          mode={authMode}
          onClose={(newMode) => { if (newMode) setAuthMode(newMode); else setShowAuth(false); }}
          onSuccess={(userData) => { setUser(userData); setShowAuth(false); }}
        />
      )}
    </>
  );

  // ─── Logged-in layout (sidebar) ────────────────────────────────────────────
  if (user) {
    return (
      <div className="app-shell">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <button className="sidebar-backdrop-btn" onClick={() => setSidebarOpen(false)} type="button" aria-label="Close sidebar" />
        )}

        <Sidebar
          user={user}
          conversations={conversations}
          activeConversationId={activeConversationId}
          onNewChat={startNewChat}
          onLoad={loadConversation}
          onDelete={handleDeleteConversation}
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          darkMode={darkMode}
          onToggleDarkMode={toggleTheme}
        />

        <div className="main-content">
          {!started ? (
            // ─── Home (logged in) ─────────────────────────────────────────
            <div className="landing-sidebar">
              <div className="landing-sidebar-topbar">
                {!sidebarOpen && (
                  <>
                    <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} type="button" title="Open sidebar">☰</button>
                    <DarkModeToggle darkMode={darkMode} onToggle={toggleTheme} />
                  </>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto", alignItems: "center" }}>
                  <ProfileSwitch value={searchProfile} onChange={setSearchProfile} />
                  <button className="nav-icon-button" onClick={() => setShowSaved(true)} type="button" aria-label="Saved">🔖</button>
                  <SearchStreak streak={streak} />
                  <UserMenu {...userMenuProps} />
                </div>
              </div>

              <main className="landing-main">
                <section className="hero-section">
                  <Logo size={92} />
                  <SearchBox query={query} setQuery={setQuery} onSearch={search} inputRef={inputRef} onMicClick={startListening} isListening={isListening} />
                </section>
                <TrendingFeed onSearch={search} />
              </main>

              <footer className="footer" style={{ position: "static", marginTop: "auto" }}>
                <span>Powered by Oryyn AI</span>
                <nav><a href="#about">About</a><a href="#privacy">Privacy</a><a href="#terms">Terms</a></nav>
              </footer>
            </div>
          ) : (
            // ─── Results (logged in) ──────────────────────────────────────
            <>
              <header className="results-navbar sticky-nav">
                {!sidebarOpen && (
                  <>
                    <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} type="button" title="Open sidebar">☰</button>
                    <DarkModeToggle darkMode={darkMode} onToggle={toggleTheme} />
                  </>
                )}
                <button className="logo-button" onClick={startNewChat} type="button"><Logo size={24} /></button>
                <SearchBox query={query} setQuery={setQuery} onSearch={search} inputRef={inputRef} compact onMicClick={startListening} isListening={isListening} />
                <div className="navbar-actions">
                  <ProfileSwitch value={searchProfile} onChange={setSearchProfile} />
                  <button className="nav-icon-button" onClick={() => setShowSaved(true)} type="button" aria-label="Saved">🔖</button>
                  <SearchStreak streak={streak} />
                  <UserMenu {...userMenuProps} />
                </div>
              </header>

              <nav className="tabs sticky-tabs">
                {Object.keys(TAB_CATEGORIES).map((tab) => (
                  <button key={tab} className={activeTab === tab ? "active" : ""} onClick={() => handleTabChange(tab)} type="button">{tab}</button>
                ))}
              </nav>

              <main className="results-area results-two-col sidebar-results">
                <div className="results-main-col">
                  {error && (
                    <div className="error-banner">
                      <span>⚠️</span>
                      <div>
                        <strong>{error}</strong>
                        {!isPro && error.includes("limit") && (
                          <span style={{ marginLeft: 8 }}><a href="/pro" style={{ color: "var(--accent)", fontWeight: 700 }}>Upgrade to Pro →</a></span>
                        )}
                      </div>
                    </div>
                  )}
                  <AdBanner isPro={isPro} />
                  {messages.map((message, index) => {
                    if (message.role === "user") return <h2 key={`u-${index}`} className="result-query">{message.content}</h2>;
                    if (message.loading) return <SkeletonLoader key={`t-${index}`} />;
                    return <AIAnswer key={`a-${index}`} msg={message} isLatest={index === messages.length - 1} hideSources={activeTab === "All" && !isMobile} onReask={search} />;
                  })}
                  {!loading && related.length > 0 && <RelatedQuestions questions={related} onAsk={search} />}
                  <FollowUpBar onSearch={search} />
                  <div ref={bottomRef} />
                </div>
                {activeTab === "All" && (() => {
                  const src = messages.filter((m) => m.role === "assistant" && m.sources?.length).at(-1)?.sources || [];
                  const lq = messages.filter((m) => m.role === "user").at(-1)?.content || "";
                  return (
                    <div className="results-side-col">
                      <SourcePanel sources={src} loading={loading && !src.length} lastQuery={lq} category={TAB_CATEGORIES[activeTab] || "general"} onSummarize={summarizeSelectedSources} searchProfile={searchProfile} />
                    </div>
                  );
                })()}
              </main>
            </>
          )}
        </div>

        {modals}
      </div>
    );
  }

  // ─── Logged-out: landing ────────────────────────────────────────────────────
  if (!started) {
    return (
      <div className="landing-page">
        <div className="landing-topbar">
          <DarkModeToggle darkMode={darkMode} onToggle={toggleTheme} />
          <div className="landing-topbar-right">
            <ProfileSwitch value={searchProfile} onChange={setSearchProfile} />
            <button className="nav-icon-button" onClick={() => setShowSaved(true)} type="button" aria-label="Saved">🔖</button>
            <SearchStreak streak={streak} />
            <button className="btn-signin" onClick={() => { setAuthMode("login"); setShowAuth(true); }} type="button">Sign in</button>
            <button className="btn-signup" onClick={() => { setAuthMode("signup"); setShowAuth(true); }} type="button">Sign up</button>
          </div>
        </div>
        <main className="landing-main">
          <section className="hero-section">
            <Logo size={isMobile ? 64 : 92} />
            <SearchBox query={query} setQuery={setQuery} onSearch={search} inputRef={inputRef} onMicClick={startListening} isListening={isListening} />
          </section>
          <TrendingFeed onSearch={search} />
        </main>
        <footer className="footer landing-footer">
          <span>Powered by Oryyn AI</span>
          <nav><a href="#about">About</a><a href="#privacy">Privacy</a><a href="#terms">Terms</a></nav>
        </footer>
        {modals}
      </div>
    );
  }

  // ─── Logged-out: results ────────────────────────────────────────────────────
  return (
    <div className="results-page">
      <header className="results-navbar">
        <DarkModeToggle darkMode={darkMode} onToggle={toggleTheme} />
        <button className="logo-button" onClick={startNewChat} type="button"><Logo size={24} /></button>
        <SearchBox query={query} setQuery={setQuery} onSearch={search} inputRef={inputRef} compact onMicClick={startListening} isListening={isListening} />
        <div className="navbar-actions">
          <ProfileSwitch value={searchProfile} onChange={setSearchProfile} />
          <button className="nav-icon-button" onClick={() => setShowSaved(true)} type="button" aria-label="Saved">🔖</button>
          <SearchStreak streak={streak} />
          <button className="btn-signup" onClick={() => { setAuthMode("login"); setShowAuth(true); }} type="button">Sign in</button>
        </div>
      </header>
      <nav className="tabs">
        {Object.keys(TAB_CATEGORIES).map((tab) => (
          <button key={tab} className={activeTab === tab ? "active" : ""} onClick={() => handleTabChange(tab)} type="button">{tab}</button>
        ))}
      </nav>
      <main className="results-area results-two-col">
        <div className="results-main-col">
          {error && (
            <div className="error-banner">
              <span>⚠️</span><div><strong>{error}</strong></div>
            </div>
          )}
          <AdBanner isPro={false} />
          {messages.map((message, index) => {
            if (message.role === "user") return <h2 key={`u-${index}`} className="result-query">{message.content}</h2>;
            if (message.loading) return <SkeletonLoader key={`t-${index}`} />;
            return <AIAnswer key={`a-${index}`} msg={message} isLatest={index === messages.length - 1} hideSources={activeTab === "All" && !isMobile} onReask={search} />;
          })}
          {!loading && related.length > 0 && <RelatedQuestions questions={related} onAsk={search} />}
          <FollowUpBar onSearch={search} />
          <div ref={bottomRef} />
        </div>
        {activeTab === "All" && (() => {
          const src = messages.filter((m) => m.role === "assistant" && m.sources?.length).at(-1)?.sources || [];
          const lq = messages.filter((m) => m.role === "user").at(-1)?.content || "";
          return (
            <div className="results-side-col">
              <SourcePanel sources={src} loading={loading && !src.length} lastQuery={lq} category={TAB_CATEGORIES[activeTab] || "general"} onSummarize={summarizeSelectedSources} searchProfile={searchProfile} />
            </div>
          );
        })()}
      </main>
      {modals}
    </div>
  );
}

export default App;
